import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

export default function GalleryView({ images = [], title = 'Galleri' }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!images || images.length === 0) {
    return (
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-8 text-center text-slate-400">
        Der er i øjeblikket ingen billeder i dette galleri.
      </div>
    );
  }

  const openLightbox = (index) => {
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden'; // Forhindr scroll i baggrunden
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    document.body.style.overflow = ''; // Gendan scroll
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="w-full">
      {/* Grid of Images */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {images.map((imgUrl, index) => {
          // Brug Cloudflare on-the-fly resizing til galleri-thumbnails (~600px bredde)
          const thumbnailUrl = imgUrl.startsWith('https://assets.auxodev.dk')
            ? imgUrl.replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/')
            : imgUrl;

          return (
            <div
              key={index}
              onClick={() => openLightbox(index)}
              className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-slate-100 bg-slate-50 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
            >
              <img
                src={thumbnailUrl}
                alt={`${title} - Billede ${index + 1}`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-brand-charcoal/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="rounded-full bg-white/20 backdrop-blur-md p-3 text-white scale-90 group-hover:scale-100 transition-transform duration-300">
                  <Maximize2 className="h-6 w-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 md:p-8 animate-in fade-in duration-200"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 z-50 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors cursor-pointer"
            aria-label="Luk visning"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 md:left-8 z-50 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors cursor-pointer"
                aria-label="Forrige billede"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 md:right-8 z-50 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors cursor-pointer"
                aria-label="Næste billede"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Large Image Container */}
          <div className="relative max-h-[85vh] max-w-[90vw] flex flex-col items-center justify-center">
            {/* Brug Cloudflare resizing til stor visning (~1600px bredde) */}
            {(() => {
              const largeImgUrl = images[lightboxIndex];
              const optimizedLargeUrl = largeImgUrl.startsWith('https://assets.auxodev.dk')
                ? largeImgUrl.replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/')
                : largeImgUrl;

              return (
                <img
                  src={optimizedLargeUrl}
                  alt={`${title} - Stor visning ${lightboxIndex + 1}`}
                  className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl animate-in zoom-in-95 duration-200"
                  onClick={(e) => e.stopPropagation()} // Undgå lukning ved klik på selve billedet
                />
              );
            })()}
            
            {/* Image Counter */}
            <p className="text-slate-400 text-sm mt-4 font-medium">
              Billede {lightboxIndex + 1} af {images.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
